import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { authReducer } from 'reducers/authReducer';
import { FlashMessageContext } from 'contexts/FlashMessageContext';
import { getValueByKey } from 'utils/helpers';
import { LOCAL_STORAGE_TOKEN } from 'lib/axios';
import { getProfile } from "../utils/api";

export const AuthContext = createContext();

const AuthContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { addFlashMessage } = useContext(FlashMessageContext);

    const [state, dispatch] = useReducer(authReducer, {
        isAuthenticated: false,
        tokenData: '',
        authChecked: false,
        profile: {},
    });

    useEffect(() => {
        //Check if token exists in local storage
        const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);

        if (!token || token === 'undefined') {
            dispatch({ type: 'CHECK_AUTH' });
            return;
        }

        if (token) {
            dispatch({ type: 'SET_AUTH', payload: token });
        }

        dispatch({ type: 'CHECK_AUTH' });
    }, []);

    useEffect(() => {
        //fetch Profile data when state gets token object
        if (state.tokenData && state.isAuthenticated) {
            fetchProfile();

        }
    }, [state.tokenData]);

    const fetchProfile = async () => {
        try {
            const res = await getProfile();

            res?.profile && dispatch({ type: 'SET_PROFILE', payload: res.profile });
        } catch (err) {
            handleError(err);
        }
    };

    const signIn = (token) => {
        dispatch({ type: 'SET_AUTH', payload: token });
        if (localStorage.getItem(LOCAL_STORAGE_TOKEN)) {
            localStorage.removeItem(LOCAL_STORAGE_TOKEN);
            localStorage.setItem(LOCAL_STORAGE_TOKEN, token);
        } else {
            localStorage.setItem(LOCAL_STORAGE_TOKEN, token);
        }
    };

    const logout = async () => {
        dispatch({ type: 'LOGOUT' });
        localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    };

    useEffect(() => {
        const handleInvalidToken = (e) => {
            if (e.key === 'token' && e.oldValue && !e.newValue) {
                logout();
            }
        };
        window.addEventListener('storage', handleInvalidToken);

        return function cleanup() {
            window.removeEventListener('storage', handleInvalidToken);
        };
    }, []);

    const handleError = (errObj, setError, redirectOnly, isSignIn) => {
        if (redirectOnly) {
            navigate('/404');
            return;
        }

        const code = errObj ? errObj.code : undefined;
        switch (code) {
            case 'INTERNAL_ERROR':
                addFlashMessage('danger', errObj?.message || 'Unable to complete your request at this time');
                break;
            case 'LOGICAL_ERROR':
                errObj.message && addFlashMessage('danger', errObj.message);
                break;
            case 'ACCESS_DENIED':
                if (errObj?.message?.includes('You are not allowed to use this feature.')) {
                    addFlashMessage('danger', errObj.message);
                } else {
                    localStorage.removeItem(LOCAL_STORAGE_TOKEN);
                    navigate('/signin');
                }
                break;
            case 'AUTHENTICATION_FAILED':
                localStorage.removeItem(LOCAL_STORAGE_TOKEN);
                isSignIn && errObj.message && addFlashMessage('danger', errObj.message);
                navigate('/signin');
                break;
            case 'VALIDATION_FAILED':
                setError && setError(errObj);

                const message = getValueByKey(errObj.errors, 'message');
                message && addFlashMessage('danger', message);
                break;
            case 'NOT_FOUND':
                navigate('/404');
                break;
            case 'USERNAME_INVALID':
                addFlashMessage('danger', 'The email or password did not match our records. Please try again.');
                break;
            case 'PASSWORD_INVALID':
                addFlashMessage('danger', 'The email or password did not match our records. Please try again.');
                break;
            default:
                addFlashMessage('danger', errObj?.message || 'Unable to complete your request at this time');
        }
    };

    return (
        <AuthContext.Provider
            value={{
                state,
                signIn,
                logout,
                handleError,
                fetchProfile,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;
